import type * as THREE from 'three';
import type { StateValue } from '@vrlearning/lesson-schema';
import { stateFeature } from '../features.js';
import type { WorldBuilder } from '../world.js';

/**
 * A block a feature can change: one stateful setting at a time, by name (lesson-schema's STATEFUL).
 * Numbers arrive eased; a colour as "#rrggbb", eased per channel; a boolean as 0–1, eased.
 * `snap` is called when the class jumps to a state rather than moving on by one step: a block with
 * its own lag (a mill's sails, an eruption's age) catches up at once.
 */
export type StatefulBlock = { set(setting: string, value: number | string): void; snap?(): void };

export type BlockFeature = {
  readonly initial: string;
  readonly seconds: number;
  /** Per state: `"<blockId>.<setting>": value`. */
  readonly states: Readonly<Record<string, Readonly<Record<string, StateValue>>>>;
  /** What is seen of it, where lesson-schema's WORLDS says it is. */
  readonly anchor: THREE.Object3D;
};

type Kind = 'number' | 'colour' | 'boolean';
const kindOf = (value: StateValue): Kind =>
  typeof value === 'boolean' ? 'boolean' : typeof value === 'string' ? 'colour' : 'number';
const channel = (colour: string, i: number): number =>
  Number.parseInt(colour.slice(1 + i * 2, 3 + i * 2), 16);
const hex = (r: number, g: number, b: number): string =>
  `#${[r, g, b]
    .map((c) =>
      Math.round(Math.min(255, Math.max(0, c)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;

/** A state's values as numbers to ease: a colour as three channels `key#0..2`, a boolean as 0/1. */
function encode(
  values: Readonly<Record<string, StateValue>>,
  kinds: ReadonlyMap<string, Kind>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, kind] of kinds) {
    const value = values[key]!;
    if (kind === 'colour')
      for (let i = 0; i < 3; i++) out[`${key}#${i}`] = channel(value as string, i);
    else out[key] = kind === 'boolean' ? (value ? 1 : 0) : (value as number);
  }
  return out;
}

/**
 * A world's features as block settings (V1): each eases its blocks' settings to the state's values
 * (stateFeature, `seconds` when the class moves on by one step), the code worlds' and the packages'.
 * A setting a state does not name keeps the value the first state gave it (else any state's).
 */
export function blockFeatures(
  world: WorldBuilder,
  features: Readonly<Record<string, BlockFeature>>,
  blocks: ReadonlyMap<string, StatefulBlock>,
): void {
  for (const [name, feature] of Object.entries(features)) {
    const all = Object.values(feature.states);
    const keys = [...new Set(all.flatMap((s) => Object.keys(s)))];
    const first = feature.states[feature.initial] ?? {};
    const filled = (changes: Readonly<Record<string, StateValue>>): Record<string, StateValue> =>
      Object.fromEntries(
        keys.map((key) => [
          key,
          changes[key] ?? first[key] ?? all.find((s) => s[key] !== undefined)![key]!,
        ]),
      );
    const kinds = new Map(keys.map((key) => [key, kindOf(filled(first)[key]!)]));
    const states = Object.fromEntries(
      Object.entries(feature.states).map(([state, changes]) => [
        state,
        encode(filled(changes), kinds),
      ]),
    );
    const touched = new Set(keys.map((key) => key.split('.')[0]!));
    const control = stateFeature<string>({
      initial: feature.initial,
      states,
      seconds: feature.seconds,
      onChange: (_state, animate) => {
        if (animate) return;
        // A jump: the blocks are at the state's values now, then catch up at once.
        apply();
        for (const id of touched) blocks.get(id)?.snap?.();
      },
    });
    world.addFeature(name, control.control, feature.anchor);
    // From the first frame the blocks stand at the state's values.
    const apply = (): void => {
      for (const [key, kind] of kinds) {
        const [id, setting] = key.split('.') as [string, string];
        const block = blocks.get(id);
        if (!block) continue;
        if (kind === 'colour') {
          const [r, g, b] = [0, 1, 2].map((i) => control.levels.get(`${key}#${i}`));
          block.set(setting, hex(r!, g!, b!));
        } else block.set(setting, control.levels.get(key));
      }
    };
    apply();
    world.onFeatureFrame((dt) => {
      // Only while the levels move (and the frame they stop on): no colour strings built for a still world.
      const was = control.levels.moving;
      control.update(dt);
      if (was || control.levels.moving) apply();
    });
  }
}
